import React, { useState, useEffect, useMemo } from 'react';
import { postFetch } from '../../../../fetch/fetch';
import totalLocationData from '../../../../data/locationdata';
import {
    BarChart, Bar, LineChart, Line,
    CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── 지표 색상 ─────────────────────────────────────────────────────────────────
const C = {
    call:       "#6E8FB6",
    waiting:    "#BA7517",
    visit:      "#185FA5",
    phone:      "#534AB7",
    newStudent: "#1D9E75",
    exit:       "#E24B4A",
};

// ─── 라이트 모드 테마 상수 ─────────────────────────────────────────────────────
const PAGE_BG   = "#F3F4F6";
const CARD_BG   = "#FFFFFF";
const BORDER    = "#E5E7EB";
const TEXT_MAIN = "#111827";
const TEXT_SUB  = "#6B7280";

type PeriodType = "thisWeek" | "thisMonth" | "lastMonth" | "last90" | "thisYear" | "custom";

// ─── 날짜 유틸 ─────────────────────────────────────────────────────────────────
function toStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function toDisp(s: string): string {
    return s.replace(/-/g, ".");
}

function calcPeriod(p: PeriodType): { start: string; end: string } {
    const today = new Date();
    const todayStr = toStr(today);
    if (p === "thisWeek") {
        const day = today.getDay();
        const mon = new Date(today);
        mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
        return { start: toStr(mon), end: todayStr };
    }
    if (p === "thisMonth") {
        return { start: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`, end: todayStr };
    }
    if (p === "lastMonth") {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last  = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: toStr(first), end: toStr(last) };
    }
    if (p === "last90") {
        const d90 = new Date(today);
        d90.setDate(today.getDate() - 89);
        return { start: toStr(d90), end: todayStr };
    }
    if (p === "thisYear") {
        return { start: `${today.getFullYear()}-01-01`, end: todayStr };
    }
    return { start: todayStr, end: todayStr };
}

function aggregateWeekly(daily: any[]): any[] {
    const weeks: { [key: string]: any } = {};
    for (const d of daily) {
        const dt = new Date(d.date + "T00:00:00");
        const day = dt.getDay();
        const mon = new Date(dt);
        mon.setDate(dt.getDate() - (day === 0 ? 6 : day - 1));
        const key = toStr(mon);
        if (!weeks[key]) weeks[key] = { date: key, callCount: 0, waitingCount: 0, visitCount: 0, phoneCount: 0, newCount: 0, exitCount: 0 };
        weeks[key].callCount    += d.callCount;
        weeks[key].waitingCount += d.waitingCount;
        weeks[key].visitCount   += d.visitCount;
        weeks[key].phoneCount   += d.phoneCount;
        weeks[key].newCount     += d.newCount;
        weeks[key].exitCount    += d.exitCount;
    }
    return Object.values(weeks).sort((a, b) => a.date.localeCompare(b.date));
}

function aggregateMonthly(daily: any[]): any[] {
    const months: { [key: string]: any } = {};
    for (const d of daily) {
        const key = d.date.slice(0, 7);
        if (!months[key]) months[key] = { date: key, callCount: 0, waitingCount: 0, visitCount: 0, phoneCount: 0, newCount: 0, exitCount: 0 };
        months[key].callCount    += d.callCount;
        months[key].waitingCount += d.waitingCount;
        months[key].visitCount   += d.visitCount;
        months[key].phoneCount   += d.phoneCount;
        months[key].newCount     += d.newCount;
        months[key].exitCount    += d.exitCount;
    }
    return Object.values(months).sort((a, b) => a.date.localeCompare(b.date));
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────
const InflowPage: React.FC<any> = (props: any) => {

    const initPeriod = calcPeriod("thisMonth");
    const [period,      setPeriod]      = useState<PeriodType>("thisMonth");
    const [startDate,   setStartDate]   = useState<string>(initPeriod.start);
    const [endDate,     setEndDate]     = useState<string>(initPeriod.end);
    const [customStart, setCustomStart] = useState<string>(initPeriod.start);
    const [customEnd,   setCustomEnd]   = useState<string>(initPeriod.end);
    const [showCustom,  setShowCustom]  = useState<boolean>(false);

    const [selectedLocation, setSelectedLocation] = useState<string>("all");
    const [summaryData, setSummaryData] = useState<any>(null);
    const [dailyData,   setDailyData]   = useState<any>(null);
    const [loading,     setLoading]     = useState<boolean>(false);

    const [sortField, setSortField] = useState<string>("");
    const [sortDir,   setSortDir]   = useState<"asc" | "desc">("desc");

    // 차트 표시 지표 토글 (기본: 신규 + 퇴원)
    type MetricKey = "call" | "waiting" | "visit" | "phone" | "newStudent" | "exit";
    const [visibleMetrics, setVisibleMetrics] = useState<Set<MetricKey>>(
        new Set(["newStudent", "exit"] as MetricKey[])
    );
    const toggleMetric = (key: MetricKey) => {
        setVisibleMetrics(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const academyLocations = useMemo(() => {
        if (!props.user?.academy) return [];
        return totalLocationData.filter((loc) => loc.academy.includes(props.user.academy));
    }, [props.user?.academy]);

    // activateMenuList는 ChartsMain에서 호출하므로 여기서 제거

    // 날짜 변경 시 통합 엔드포인트 1회 호출 (summary + daily 동시 반환)
    useEffect(() => {
        if (!startDate || !endDate) return;
        fetchAll(startDate, endDate);
    }, [startDate, endDate]); // eslint-disable-line
    // selectedLocation 변경은 서버 호출 없이 chartData useMemo가 재계산

    const fetchAll = async (start: string, end: string) => {
        setLoading(true);
        try {
            const result: any = await postFetch("/charts/inflow/all", { startDate: start, endDate: end });
            if (result.message === "success") {
                setSummaryData(result);     // byLocation, totals, momChange
                setDailyData(result.daily); // 일별 breakdown
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    };

    const applyPeriod = (p: PeriodType) => {
        setPeriod(p);
        if (p === "custom") { setShowCustom(true); return; }
        setShowCustom(false);
        const { start, end } = calcPeriod(p);
        setStartDate(start);
        setEndDate(end);
    };

    const applyCustom = () => {
        if (!customStart || !customEnd) return;
        setStartDate(customStart);
        setEndDate(customEnd);
        setShowCustom(false);
    };

    // 캐시된 daily 데이터에서 selectedLocation 기준으로 클라이언트 필터링 (서버 재호출 없음)
    const locationFilteredDaily = useMemo(() => {
        if (!dailyData) return [];
        const key = selectedLocation === "all" ? "total" : selectedLocation;
        return dailyData.map((d: any) => ({
            date: d.date,
            callCount:    d[key]?.callCount    ?? 0,
            waitingCount: d[key]?.waitingCount ?? 0,
            visitCount:   d[key]?.visitCount   ?? 0,
            phoneCount:   d[key]?.phoneCount   ?? 0,
            newCount:     d[key]?.newCount     ?? 0,
            exitCount:    d[key]?.exitCount    ?? 0,
        }));
    }, [dailyData, selectedLocation]);

    const chartData = useMemo(() => {
        if (!locationFilteredDaily.length) return [];
        const days = locationFilteredDaily.length;
        if (days > 365) return aggregateMonthly(locationFilteredDaily);
        if (days > 90)  return aggregateWeekly(locationFilteredDaily);
        return locationFilteredDaily;
    }, [locationFilteredDaily]);

    const tableRows = useMemo(() => {
        if (!summaryData?.byLocation) return [];
        const rows = summaryData.byLocation.map((r: any) => ({
            ...r,
            net: (r.newCount || 0) - (r.exitCount || 0),
        }));
        if (!sortField) return rows;
        return [...rows].sort((a: any, b: any) => {
            const diff = (a[sortField] ?? 0) - (b[sortField] ?? 0);
            return sortDir === "asc" ? diff : -diff;
        });
    }, [summaryData, sortField, sortDir]);

    const toggleSort = (field: string) => {
        if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortField(field); setSortDir("desc"); }
    };

    const totals    = summaryData?.totals;
    const momChange = summaryData?.momChange;

    // 6개 지표 메타 (토글 UI + 차트 렌더링 공통 사용)
    const METRICS: { key: MetricKey; label: string; color: string; dataKey: string; dash?: boolean }[] = [
        { key: "call",       label: "외부문의", color: C.call,       dataKey: "callCount",    dash: true  },
        { key: "waiting",    label: "대기자",   color: C.waiting,    dataKey: "waitingCount", dash: false },
        { key: "visit",      label: "방문상담", color: C.visit,      dataKey: "visitCount",   dash: false },
        { key: "phone",      label: "전화상담", color: C.phone,      dataKey: "phoneCount",   dash: false },
        { key: "newStudent", label: "신규",     color: C.newStudent, dataKey: "newCount",     dash: false },
        { key: "exit",       label: "퇴원",     color: C.exit,       dataKey: "exitCount",    dash: false },
    ];

    // 토글 버튼 공통 렌더러
    const MetricToggle = () => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {METRICS.map(({ key, label, color }) => {
                const on = visibleMetrics.has(key);
                return (
                    <button
                        key={key}
                        onClick={() => toggleMetric(key)}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12,
                            border: `1.5px solid ${on ? color : BORDER}`,
                            background: on ? color + "18" : "transparent",
                            color: on ? color : TEXT_SUB,
                            fontWeight: on ? 600 : 400,
                            transition: "all 0.15s",
                        }}
                    >
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: on ? color : BORDER, flexShrink: 0 }} />
                        {label}
                    </button>
                );
            })}
        </div>
    );

    // ─── KPI 카드 ────────────────────────────────────────────────────────────────
    const KpiCard = ({ color, label, count, momPct, note }: {
        color: string; label: string; count: number; momPct?: number; note?: string;
    }) => {
        const positive = (momPct ?? 0) >= 0;
        return (
            <div style={{
                background: CARD_BG, borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 0,
                border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: TEXT_SUB }}>{label}</span>
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: TEXT_MAIN, lineHeight: 1.2 }}>
                    {count ?? "—"}
                </div>
                {momPct !== undefined && (
                    <div style={{ marginTop: 6, fontSize: 13, color: positive ? "#059669" : "#DC2626", fontWeight: 600 }}>
                        {positive ? "+" : ""}{momPct}% MoM
                    </div>
                )}
                {note && <div style={{ marginTop: 4, fontSize: 11, color: TEXT_SUB }}>{note}</div>}
            </div>
        );
    };

    // ─── 테이블 헤더 셀 ──────────────────────────────────────────────────────────
    const ThCell = ({ field, label, extraStyle }: {
        field: string; label: string; extraStyle?: React.CSSProperties;
    }) => (
        <th
            onClick={() => toggleSort(field)}
            style={{
                padding: "10px 14px", textAlign: "right", cursor: "pointer",
                color: TEXT_SUB, fontSize: 13, fontWeight: 600, userSelect: "none",
                borderBottom: `2px solid ${BORDER}`,
                ...extraStyle,
            }}
        >
            {label}{sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
        </th>
    );

    const periodBtnStyle = (p: PeriodType): React.CSSProperties => ({
        padding: "6px 14px", borderRadius: 20,
        border: `1px solid ${period === p ? "#4F46E5" : BORDER}`,
        background: period === p ? "#4F46E5" : CARD_BG,
        color: period === p ? "#fff" : TEXT_SUB,
        cursor: "pointer", fontSize: 13, fontWeight: period === p ? 600 : 400,
    });

    const locationTotalRow = useMemo(() => {
        if (!totals) return null;
        return {
            callCount:    totals.callCount,
            waitingCount: totals.waitingCount,
            visitCount:   totals.visitCount,
            phoneCount:   totals.phoneCount,
            newCount:     totals.newCount,
            exitCount:    totals.exitCount,
            net: (totals.newCount || 0) - (totals.exitCount || 0),
        };
    }, [totals]);

    return (
        <div style={{ padding: "24px 28px", background: PAGE_BG, color: TEXT_MAIN, minHeight: "100%" }}>

            {/* 잠정치 안내 */}
            <div style={{ marginBottom: 16, fontSize: 13, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 14px", display: "inline-block" }}>
                ⚠️ 최근 데이터는 잠정치일 수 있습니다.
            </div>

            {/* ───────────── 영역 1: 기간 필터 바 ───────────── */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                <span style={{ fontSize: 13, color: TEXT_SUB, marginRight: 4 }}>기간</span>
                {(["thisWeek", "thisMonth", "lastMonth", "last90", "thisYear", "custom"] as PeriodType[]).map((p) => (
                    <button key={p} style={periodBtnStyle(p)} onClick={() => applyPeriod(p)}>
                        {p === "thisWeek" ? "이번 주" : p === "thisMonth" ? "이번 달" : p === "lastMonth" ? "지난 달" : p === "last90" ? "최근 90일" : p === "thisYear" ? "올해" : "사용자 지정"}
                    </button>
                ))}
                <span style={{ marginLeft: "auto", fontSize: 13, color: TEXT_SUB }}>
                    {toDisp(startDate)} — {toDisp(endDate)}
                </span>
            </div>

            {showCustom && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                        style={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "6px 10px", fontSize: 13 }} />
                    <span style={{ color: TEXT_SUB }}>—</span>
                    <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                        style={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "6px 10px", fontSize: 13 }} />
                    <button onClick={applyCustom}
                        style={{ padding: "6px 16px", background: "#4F46E5", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13 }}>
                        적용
                    </button>
                </div>
            )}

            {/* ───────────── 영역 2: KPI 카드 ───────────── */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 10, fontWeight: 600, letterSpacing: 1 }}>유입 (INPUT)</div>
                <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    <KpiCard color={C.call}    label="외부문의 전화" count={totals?.callCount    ?? 0} momPct={momChange?.callPct}    note="5/2부터 집계" />
                    <KpiCard color={C.waiting} label="대기자 신청"   count={totals?.waitingCount ?? 0} momPct={momChange?.waitingPct} />
                    <KpiCard color={C.visit}   label="방문상담"       count={totals?.visitCount   ?? 0} momPct={momChange?.visitPct}   />
                    <KpiCard color={C.phone}   label="전화상담"       count={totals?.phoneCount   ?? 0} momPct={momChange?.phonePct}   />
                </div>

                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 10, fontWeight: 600, letterSpacing: 1 }}>결과 (OUTCOME)</div>
                <div style={{ display: "flex", gap: 12 }}>
                    <KpiCard color={C.newStudent} label="신규" count={totals?.newCount  ?? 0} momPct={momChange?.newPct}  />
                    <KpiCard color={C.exit}        label="퇴원" count={totals?.exitCount ?? 0} momPct={momChange?.exitPct} />
                    <div style={{ flex: 2 }} />
                </div>
            </div>

            {/* ───────────── 영역 3: 지점별 흐름 바 차트 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>지점별 흐름</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 12 }}>유입 4종 + 결과 2종을 지점별로 비교</div>

                <MetricToggle />

                <ResponsiveContainer width="100%" height={340}>
                    <BarChart
                        data={(summaryData?.byLocation || []).map((r: any) => ({
                            ...r,
                            name: academyLocations.find((l) => l.english === r.location)?.korean || r.location,
                        }))}
                        margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis dataKey="name" tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                        <YAxis tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }} />
                        {METRICS.map(m => (
                            <Bar key={m.key} dataKey={m.dataKey} fill={m.color} name={m.label} hide={!visibleMetrics.has(m.key)} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* ───────────── 영역 4: 기간 내 일별 추이 라인 차트 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>기간 내 일별 추이</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 12 }}>지점 칩을 클릭하면 해당 지점만 필터링</div>

                {/* 지점 필터 칩 */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingBottom: 14, borderBottom: `1px dashed ${BORDER}`, marginBottom: 12 }}>
                    <span style={{ fontSize: 12, color: TEXT_SUB, marginRight: 4 }}>지점</span>
                    {["all", ...academyLocations.map((l) => l.english)].map((locEng) => {
                        const label  = locEng === "all" ? "전체" : (academyLocations.find((l) => l.english === locEng)?.korean || locEng);
                        const active = selectedLocation === locEng;
                        return (
                            <button
                                key={locEng}
                                onClick={() => setSelectedLocation(locEng)}
                                style={{
                                    padding: "4px 14px", borderRadius: 20,
                                    border: `1px solid ${active ? "#4F46E5" : BORDER}`,
                                    background: active ? "#4F46E5" : CARD_BG,
                                    color: active ? "#fff" : TEXT_SUB,
                                    cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400,
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 10 }}>
                    현재: {selectedLocation === "all" ? "전사 합계" : academyLocations.find((l) => l.english === selectedLocation)?.korean || selectedLocation}
                </div>

                <MetricToggle />

                <ResponsiveContainer width="100%" height={300}>
                    <LineChart key={startDate + endDate + selectedLocation + Array.from(visibleMetrics).sort().join(",")} data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis
                            dataKey="date"
                            tick={{ fill: TEXT_SUB, fontSize: 10 }}
                            tickFormatter={(v: string) => {
                                if (!v) return "";
                                const parts = v.split("-");
                                if (parts.length === 3) return `${+parts[2]}일`;
                                if (parts.length === 2) return `${+parts[1]}월`;
                                return v;
                            }}
                        />
                        <YAxis tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }} />
                        {METRICS.filter(m => visibleMetrics.has(m.key)).map(m => (
                            <Line
                                key={m.key}
                                type="monotone"
                                dataKey={m.dataKey}
                                stroke={m.color}
                                strokeWidth={2}
                                dot={false}
                                name={m.label}
                                strokeDasharray={m.dash ? "4 4" : undefined}
                                isAnimationActive={true}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* ───────────── 영역 5: 마스터 표 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "#F9FAFB" }}>
                            <th style={{ padding: "10px 14px", textAlign: "left", color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>지점</th>
                            <ThCell field="callCount"    label="외부문의" />
                            <ThCell field="waitingCount" label="대기자" />
                            <ThCell field="visitCount"   label="방문상담" />
                            <ThCell field="phoneCount"   label="전화상담" extraStyle={{ borderRight: `2px dashed ${BORDER}` }} />
                            <ThCell field="newCount"     label="신규"     extraStyle={{ borderLeft: `2px dashed ${BORDER}` }} />
                            <ThCell field="exitCount"    label="퇴원" />
                            <ThCell field="net"          label="순증" />
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((row: any, idx: number) => (
                            <tr key={row.location} style={{ background: idx % 2 === 0 ? CARD_BG : "#F9FAFB", borderBottom: `1px solid ${BORDER}` }}>
                                <td style={{ padding: "10px 14px", color: TEXT_MAIN, fontWeight: 600 }}>
                                    {academyLocations.find((l) => l.english === row.location)?.korean || row.location}
                                </td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.callCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.waitingCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.visitCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN, borderRight: `2px dashed ${BORDER}` }}>{row.phoneCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN, borderLeft: `2px dashed ${BORDER}` }}>{row.newCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.exitCount}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: row.net > 0 ? "#059669" : row.net < 0 ? "#DC2626" : TEXT_SUB }}>
                                    {row.net > 0 ? "+" : ""}{row.net}
                                </td>
                            </tr>
                        ))}
                        {locationTotalRow && (
                            <tr style={{ background: "#EEF2FF", borderTop: `2px solid ${BORDER}` }}>
                                <td style={{ padding: "11px 14px", color: TEXT_MAIN, fontWeight: 700 }}>전사</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{locationTotalRow.callCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{locationTotalRow.waitingCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{locationTotalRow.visitCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700, borderRight: `2px dashed ${BORDER}` }}>{locationTotalRow.phoneCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700, borderLeft: `2px dashed ${BORDER}` }}>{locationTotalRow.newCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{locationTotalRow.exitCount}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 700, color: locationTotalRow.net > 0 ? "#059669" : locationTotalRow.net < 0 ? "#DC2626" : TEXT_SUB }}>
                                    {locationTotalRow.net > 0 ? "+" : ""}{locationTotalRow.net}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {loading && (
                <div style={{ position: "fixed", top: 20, right: 24, background: "#4F46E5", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 13, zIndex: 9999 }}>
                    로딩 중…
                </div>
            )}
        </div>
    );
};

export default InflowPage;
